import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent, Grid, Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';

import { getHierarchyUserLevelList } from '../../store/Slice/Onboarding/OnboardingSlice';
import HierarchyUserLevelListData from '../../store/Slice/Onboarding/OnboardingSelector';

/* =======================
   Tree Node Component
======================= */
function TreeNode({ node, level = 0 }) {
  const [isOpen, setIsOpen] = useState(false);
  if (!node) return null;

  const isEmployee = node.userTypeLabel === 'EMPLOYEE';
  const userTypeColor = isEmployee ? '#13519d' : '#475569';
  const isSelected = Boolean(node.currentUser);
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <Box sx={{ ml: level * 3, mt: 1.5 }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        whileHover={{ scale: 1.01 }}
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: hasChildren ? 'pointer' : 'default'
        }}
      >
        {/* Partner Card Wrapper */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Reporting Manager Badge */}
          {node.reportingMangerName && (
            <Box
              sx={{
                backgroundColor: 'rgba(19, 81, 157, 0.1)',
                color: '#13519d',
                padding: '3px 8px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: 600,
                alignSelf: 'flex-start',
                border: '1px solid rgba(19, 81, 157, 0.2)'
              }}
            >
              Manager: {node.reportingMangerName}
            </Box>
          )}

          {/* Partner Card */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: userTypeColor,
              p: '10px 16px',
              borderRadius: '12px',
              width: 320,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              backgroundColor: isSelected ? userTypeColor : `${userTypeColor}15`,
              color: isSelected ? '#fff' : userTypeColor,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              fontWeight: 600,
              userSelect: 'none'
            }}
          >
            {/* Arrow rotate on open */}
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
              {hasChildren ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: isSelected ? '#fff' : '#64748b'
                  }}
                >
                  <ChevronRight size={18} />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : '#e2e8f0'
                  }}
                />
              )}
            </motion.div>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>
              {node.partnerName}
            </Typography>
          </Box>
        </Box>

        {/* Current User Badge */}
        {isSelected && (
          <Box
            sx={{
              padding: '4px 10px',
              background: 'rgba(245, 158, 11, 0.1)',
              color: '#d97706',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}
          >
            CURRENT USER
          </Box>
        )}

        {/* Count */}
        <Typography variant="caption" sx={{ ml: 1, color: '#64748b' }}>
          Direct: {node.directCount} | Total: {node.totalCount}
        </Typography>

        {/* Has Children Indicator */}
        <Typography
          variant="caption"
          sx={{
            ml: 1,
            color: hasChildren ? '#16a34a' : '#94a3b8',
            fontWeight: 600
          }}
        >
          {hasChildren ? 'Has Team' : 'No Team'}
        </Typography>
      </motion.div>

      {/* Children (toggle controlled) */}
      {isOpen && hasChildren && (
        <Box
          sx={{
            borderLeft: '2px dashed #cbd5e1',
            ml: 2.5,
            pl: 2.5,
            mt: 1
          }}
        >
          {children.map((child) => (
            <TreeNode key={child.partnerId} node={child} level={level + 1} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/* =======================
   Main Component
======================= */
export default function PartnerHierarchyPage({ partnerId }) {
  const { HierarchyUserLevelListData: hierarchyData } = HierarchyUserLevelListData();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getHierarchyUserLevelList({ partnerId }));
  }, [partnerId, dispatch]);

  const rootNode = Array.isArray(hierarchyData) ? hierarchyData[0] : null;

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        p: 3
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#13519d', mb: 2.5 }}>
        Partner Hierarchy Structure
      </Typography>

      <CardContent sx={{ p: 0 }}>
        {rootNode ? (
          <TreeNode node={rootNode} />
        ) : (
          <Typography color="text.secondary">No hierarchy data available</Typography>
        )}
      </CardContent>
    </Card>
  );
}
